import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import WorkspaceNotice from '../components/WorkspaceNotice';
import { ExternalLink, FlaskConical, Image as ImageIcon, Lock, Monitor, Trash2, Users2 } from 'lucide-react';
import { workspaceAccessMessage } from '../lib/workspaceAccess';

function trustStatusLabel(status) {
  if (status === 'approved') return 'Approved';
  if (status === 'rejected') return 'Rejected';
  if (status === 'pending') return 'Pending';
  return 'Not started';
}

function trustStatusClass(status) {
  if (status === 'approved') return 'border-emerald-300 bg-emerald-50 text-emerald-700';
  if (status === 'rejected') return 'border-red-300 bg-red-50 text-red-700';
  return 'border-zinc-200 bg-white text-zinc-500';
}

function formatTrustDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === 'admin';
  const [data, setData] = useState(null);
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [trustSetup, setTrustSetup] = useState(null);
  const [trustBusy, setTrustBusy] = useState(false);
  const [trustError, setTrustError] = useState(null);
  const logoInputRef = useRef(null);

  // Add member form
  const [m, setM] = useState({ email: '', password: '', full_name: '', role: 'reviewer' });
  const [memberMsg, setMemberMsg] = useState(null);
  const [memberModalOpen, setMemberModalOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/organization');
      setData(data);
      setName(data.organization.name);
      setWebsite(data.organization.website || '');
      setSupportEmail(data.organization.support_email || '');
      const trust = await api.get('/organization/trust-setup');
      setTrustSetup(trust.data);
      setLoadError(null);
    } catch (err) {
      setLoadError(workspaceAccessMessage(err, 'Organization settings could not be loaded.'));
    }
  }, []);
  useEffect(() => {
    if (!isAdmin) return;
    load();
  }, [isAdmin, load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/organization', {
        name,
        website: website || null,
        support_email: supportEmail || null,
      });
      setSavedFlash(true); setTimeout(()=>setSavedFlash(false), 1500);
      await load();
      // refresh AuthProvider so the sandbox badge in the topbar updates immediately
      await refresh();
    } catch (err) {
      setLoadError(workspaceAccessMessage(err, 'Could not save organization settings.'));
    } finally { setSaving(false); }
  };

  const toggleSandbox = async () => {
    const next = !data.organization.sandbox_mode;
    if (!next && !window.confirm('Disable sandbox mode? New sessions will use the full KYC verification pipeline.')) return;
    if (next && !window.confirm('Enable sandbox mode? Future sessions will be DETERMINISTIC fixtures based on the uploaded filename.')) return;
    try {
      await api.patch('/organization', { sandbox_mode: next });
      await load();
      await refresh();
    } catch (err) {
      setLoadError(workspaceAccessMessage(err, 'Could not update sandbox mode.'));
    }
  };

  const addMember = async (e) => {
    e.preventDefault();
    setMemberMsg(null);
    try {
      await api.post('/organization/members', m);
      setMemberMsg({ type: 'ok', text: 'Member added.' });
      setM({ email: '', password: '', full_name: '', role: 'reviewer' });
      setMemberModalOpen(false);
      await load();
    } catch (err) {
      setMemberMsg({ type: 'err', text: err?.response?.data?.error || 'Failed to add' });
    }
  };

  const organizationLogoUrl = data?.organization?.logo_url
    || (data?.organization?.logo_path ? `${process.env.REACT_APP_BACKEND_URL}/api/organization/logo/${data.organization.id}` : null);

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoBusy(true);
    setLogoError(null);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      await api.post('/organization/logo', fd);
      await load();
      await refresh();
    } catch (e) {
      setLogoError(e?.response?.data?.error === 'invalid_logo_type'
        ? 'Upload a PNG, JPG or WEBP logo.'
        : 'Could not upload logo.');
    } finally {
      setLogoBusy(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    setLogoError(null);
    try {
      await api.delete('/organization/logo');
      await load();
      await refresh();
    } catch {
      setLogoError('Could not remove logo.');
    } finally {
      setLogoBusy(false);
    }
  };

  const deleteOrganization = async () => {
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.delete('/organization');
      logout();
      nav('/', { replace: true });
    } catch (e) {
      setDeleteError(e?.response?.data?.error === 'forbidden'
        ? 'Only admins can delete an organization.'
        : 'Could not delete organization.');
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleIdentityVerification = async () => {
    const identity = trustSetup?.identity;
    const session = identity?.session;
    if (identity?.status === 'pending' && ['pending', 'opened'].includes(session?.status) && session?.public_url) {
      window.open(session.public_url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (session?.public_url && identity?.status === 'approved') {
      window.open(session.public_url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (session?.public_url && identity?.status === 'pending') {
      window.open(session.public_url, '_blank', 'noopener,noreferrer');
      return;
    }
    setTrustBusy(true);
    setTrustError(null);
    try {
      const { data } = await api.post('/organization/trust-setup/identity-verification');
      setTrustSetup((current) => ({ ...(current || {}), visible: true, identity: data.identity }));
      if (data.session?.public_url) window.open(data.session.public_url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setTrustError(e?.response?.data?.error || 'Could not start identity verification.');
    } finally {
      setTrustBusy(false);
    }
  };

  if (loadError) {
    return (
      <section data-testid="settings-error" className="border border-red-200 bg-red-50 p-6 text-red-800">
        <p className="text-xs font-mono uppercase tracking-widest text-red-500">Settings</p>
        <h1 className="mt-1 font-display text-2xl font-bold tracking-tight text-red-950">Organization settings unavailable</h1>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-red-800">{loadError}</p>
      </section>
    );
  }

  if (!isAdmin) {
    return (
      <WorkspaceNotice
        testId="settings-denied"
        title="Organization settings unavailable"
        message="Only organization admins can manage organization settings."
        detail="Ask an organization admin to review your workspace role."
      />
    );
  }

  if (!data) return <div className="text-sm text-zinc-500">Loading…</div>;

  const identityStatus = trustSetup?.identity?.status || 'not_started';
  const verificationDate = formatTrustDate(trustSetup?.identity?.session?.approved_at);
  const reviewerDate = formatTrustDate(trustSetup?.identity?.session?.reviewer_decision_at);
  const showVerificationDate = identityStatus !== 'not_started';
  const showReviewerDate = identityStatus === 'approved'
    || (['pending', 'rejected'].includes(identityStatus) && Boolean(reviewerDate));

  return (
    <div data-testid="settings-page" className="space-y-6">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Settings</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Organization</h1>
      </div>

      <section className="border border-zinc-200 rounded-sm bg-white p-6">
        <h2 className="font-display text-lg font-semibold">General</h2>
        {!isAdmin && (
          <p className="text-sm text-zinc-600 mt-1">Only admins can edit organization settings.</p>
        )}
        <div className="mt-4 grid md:grid-cols-2 gap-4 items-start">
          <div>
            <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Name</label>
            <input data-testid="org-name" value={name} onChange={(e)=>setName(e.target.value)} disabled={!isAdmin}
              className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black disabled:bg-zinc-50"/>
          </div>

          <div className="md:row-span-2 md:self-stretch flex flex-col">
            <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Logo</label>
            <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50 flex flex-1 items-center">
              <div className="flex items-center gap-3 w-full">
                <div className="w-16 h-16 border border-zinc-200 bg-white rounded-sm grid place-items-center overflow-hidden shrink-0">
                  {organizationLogoUrl ? (
                    <img src={organizationLogoUrl} alt="Organization logo" className="max-w-full max-h-full object-contain"/>
                  ) : (
                    <ImageIcon className="w-6 h-6 text-zinc-400" strokeWidth={1.5}/>
                  )}
                </div>
                <div className="flex-1 min-w-0 max-w-xs">
                  <p className="text-sm text-zinc-700">{organizationLogoUrl ? 'Logo uploaded' : 'No logo uploaded'}</p>
                  <p className="text-xs text-zinc-500 font-mono mt-0.5">Used in signing invitation emails.</p>
                  {isAdmin && (
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp"
                        className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])}/>
                      <button type="button" onClick={() => logoInputRef.current?.click()} disabled={logoBusy}
                        className="h-8 text-sm px-3 border border-zinc-200 bg-white rounded-sm hover:border-zinc-400 disabled:opacity-50">
                        {logoBusy ? 'Working...' : 'Upload logo'}
                      </button>
                      {organizationLogoUrl && (
                        <button type="button" onClick={removeLogo} disabled={logoBusy}
                          className="h-8 inline-flex items-center gap-1.5 text-sm px-3 border border-zinc-200 bg-white rounded-sm hover:border-zinc-400 disabled:opacity-50">
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5}/> Remove
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {logoError && <p className="ml-3 text-xs text-red-700 font-mono">{logoError}</p>}
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Organization ID</label>
            <input value={data.organization.id} readOnly
              className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm font-mono bg-zinc-50 text-zinc-600"/>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Website</label>
            <input value={website} onChange={(e)=>setWebsite(e.target.value)} disabled={!isAdmin}
              placeholder="https://example.com"
              className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black disabled:bg-zinc-50"/>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Support email</label>
            <input type="email" value={supportEmail} onChange={(e)=>setSupportEmail(e.target.value)} disabled={!isAdmin}
              placeholder="support@example.com"
              className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black disabled:bg-zinc-50"/>
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-3 mt-4">
            <button onClick={save} disabled={saving} data-testid="save-org"
              className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            {savedFlash && <span className="text-xs text-green-700 font-mono">SAVED</span>}
          </div>
        )}
      </section>

      {trustSetup?.visible && (
        <section className="border border-zinc-200 rounded-sm bg-white p-6" data-testid="trust-setup-section">
          <div>
            <h2 className="font-display text-lg font-semibold flex items-center gap-2">
              <Monitor className="w-4 h-4 text-zinc-500" strokeWidth={1.5}/>
              Workspace verification
            </h2>
          </div>
          <div className="mt-4 border border-zinc-200 rounded-sm p-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-display text-sm font-semibold">Identity verification</span>
                  <span className={`text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 border ${trustStatusClass(identityStatus)}`}>
                    {trustStatusLabel(identityStatus)}
                  </span>
                </div>
                <p className="text-sm text-zinc-600 mt-3 max-w-xl">
                  Verify your identity to activate this workspace and unlock the free tier.
                </p>
                {(showVerificationDate || showReviewerDate) && (
                  <div className="mt-3 grid sm:grid-cols-2 gap-3 text-xs">
                    {showVerificationDate && (
                      <div className="border border-zinc-200 rounded-sm p-3">
                        <p className="font-mono uppercase tracking-widest text-zinc-500">Verification date</p>
                        <p className="mt-1 text-zinc-700">{verificationDate || '—'}</p>
                      </div>
                    )}
                    {showReviewerDate && (
                      <div className="border border-zinc-200 rounded-sm p-3">
                        <p className="font-mono uppercase tracking-widest text-zinc-500">Reviewer date</p>
                        <p className="mt-1 text-zinc-700">{reviewerDate || '—'}</p>
                      </div>
                    )}
                  </div>
                )}
                {trustError && <p className="mt-3 text-xs font-mono text-red-700">{trustError}</p>}
              </div>
              <div className="shrink-0">
                <button type="button" onClick={handleIdentityVerification}
                  disabled={!isAdmin || trustBusy}
                  className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50">
                  {trustBusy
                    ? 'Working...'
                      : trustSetup.identity?.status === 'pending' && ['pending', 'opened'].includes(trustSetup.identity?.session?.status)
                        ? <span className="inline-flex items-center gap-1.5">Continue verification <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5}/></span>
                      : trustSetup.identity?.status === 'rejected'
                        ? <span className="inline-flex items-center gap-1.5">Retry verification <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5}/></span>
                      : trustSetup.identity?.session?.public_url
                        ? <span className="inline-flex items-center gap-1.5">View status <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5}/></span>
                        : <span className="inline-flex items-center gap-1.5">Verify identity <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5}/></span>}
                </button>
                {!isAdmin && (
                  <p className="mt-2 text-xs text-zinc-500 font-mono text-right">Admin only</p>
                )}
              </div>
            </div>
          </div>
          <div className="mt-3 border border-zinc-200 rounded-sm p-4 bg-zinc-50">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-display text-sm font-semibold">Business verification</h3>
                <p className="text-xs text-zinc-500 mt-1">Coming soon</p>
              </div>
              <span className="text-[10px] uppercase tracking-widest font-mono font-semibold px-1.5 py-0.5 border border-zinc-200 bg-white text-zinc-500">
                Coming soon
              </span>
            </div>
          </div>
        </section>
      )}

      <section className="border border-zinc-200 rounded-sm bg-white p-6" data-testid="sandbox-section">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-semibold flex items-center gap-2">
              <FlaskConical className="w-4 h-4" strokeWidth={1.5}/> Sandbox mode
              {data.organization.sandbox_mode && (
                <span className="text-[10px] uppercase tracking-widest font-mono font-semibold px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800">ON</span>
              )}
            </h2>
            <p className="text-sm text-zinc-600 mt-1 max-w-xl">
              Skip real KYC verification entirely. Verifications return <strong>deterministic fixtures</strong> based on the
              uploaded ID document filename: <code className="font-mono text-xs">approved.jpg</code> &rarr; approved,
              <code className="font-mono text-xs"> rejected.jpg</code> &rarr; rejected,
              <code className="font-mono text-xs"> review.jpg</code> &rarr; needs_review.
              Use this for integration tests and demos. <strong>Never use in production.</strong>
            </p>
          </div>
          {isAdmin && (
            <button onClick={toggleSandbox} data-testid="toggle-sandbox"
              className={`text-sm px-3 py-2 rounded-sm border transition-colors ${
                data.organization.sandbox_mode
                  ? 'bg-amber-50 border-amber-400 text-amber-900 hover:bg-amber-100'
                  : 'bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400'
              }`}>
              {data.organization.sandbox_mode ? 'Disable sandbox' : 'Enable sandbox'}
            </button>
          )}
        </div>
      </section>

      <section className="border border-zinc-200 rounded-sm bg-white p-6" data-testid="retention-settings">
            <h2 className="font-display text-lg font-semibold flex items-center gap-2">
              <Lock className="w-4 h-4" strokeWidth={1.5}/> Image retention
            </h2>
            <p className="text-sm text-zinc-600 mt-1 max-w-xl">
              By default, idemGO never stores raw images. Manual review and suspected-fraud cases are kept for a short
              window automatically. Optional retention overrides that and stores ALL images for the period below.
              <strong className="text-zinc-700"> Use sparingly — it weakens your privacy posture.</strong>
            </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <label className="text-xs uppercase tracking-widest text-zinc-500">Optional retention</label>
          <select disabled={!isAdmin} value={data.organization.image_retention_days ?? 0}
            data-testid="retention-select"
            onChange={async (e) => {
              const v = Number(e.target.value);
              if (v > 0 && !window.confirm('Enable optional retention? Images for ALL future verifications will be stored.')) return;
              await api.patch('/organization', { image_retention_days: v });
              await load();
              await refresh();
            }}
            className="border border-zinc-200 rounded-sm px-3 py-2 text-sm disabled:bg-zinc-50">
            <option value={0}>Off (default · privacy-first)</option>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
          </select>
          {data.organization.image_retention_days > 0 && (
            <span data-testid="retention-active-badge"
              className="text-[10px] uppercase tracking-widest font-mono font-semibold px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800">
              Active · {data.organization.image_retention_days}d
            </span>
          )}
        </div>
        <div className="mt-4 grid sm:grid-cols-3 gap-3 text-xs">
          <div className="border border-zinc-200 rounded-sm p-3">
            <p className="font-mono uppercase tracking-widest text-zinc-500">Manual review</p>
            <p className="mt-1 text-zinc-700">Up to 72 h on <code className="font-mono">needs_review</code>. Auto-purged.</p>
          </div>
          <div className="border border-zinc-200 rounded-sm p-3">
            <p className="font-mono uppercase tracking-widest text-zinc-500">Suspected fraud</p>
            <p className="mt-1 text-zinc-700">14 d on <code className="font-mono">rejected</code> with face mismatch.</p>
          </div>
          <div className="border border-zinc-200 rounded-sm p-3">
            <p className="font-mono uppercase tracking-widest text-zinc-500">Optional retention</p>
            <p className="mt-1 text-zinc-700">All sessions for the chosen window. Off by default.</p>
          </div>
        </div>
      </section>

      <section className="border border-zinc-200 rounded-sm bg-white p-6" data-testid="age-section">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2">
          <Users2 className="w-4 h-4" strokeWidth={1.5}/> Age check
        </h2>
        <p className="text-sm text-zinc-600 mt-1 max-w-xl">
          When enabled, idemGO computes the applicant's age from the OCR-extracted date of birth.
          If the result is below the threshold, the KYC verification is forced to <code className="font-mono text-xs">rejected</code>
          regardless of the OCR / face-match outcome.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <label className="text-xs uppercase tracking-widest text-zinc-500">Minimum age</label>
          <select disabled={!isAdmin} value={data.organization.min_age ?? 0}
            data-testid="min-age-select"
            onChange={async (e) => {
              const v = Number(e.target.value);
              await api.patch('/organization', { min_age: v });
              await load();
              await refresh();
            }}
            className="border border-zinc-200 rounded-sm px-3 py-2 text-sm disabled:bg-zinc-50">
            <option value={0}>Off</option>
            <option value={16}>16+</option>
            <option value={18}>18+ (most common)</option>
            <option value={21}>21+</option>
          </select>
          {data.organization.min_age > 0 && (
            <span data-testid="min-age-active-badge"
              className="text-[10px] uppercase tracking-widest font-mono font-semibold px-1.5 py-0.5 border border-zinc-200 bg-white text-zinc-500">
              Active · {data.organization.min_age}+
            </span>
          )}
        </div>
      </section>

      <section className="border border-zinc-200 rounded-sm bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h2 className="font-display text-lg font-semibold">Members</h2>
          {isAdmin && (
            <button type="button" onClick={() => {
              setMemberMsg(null);
              setMemberModalOpen(true);
            }}
              className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800">
              Add member
            </button>
          )}
        </div>
        <div className="mt-4 border border-zinc-200 rounded-sm overflow-hidden bg-white">
          <table className="w-full text-sm" data-testid="members-table">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-xs uppercase tracking-widest text-zinc-500 text-left">
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Role</th>
                <th className="px-4 py-2 font-medium">Joined</th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((mem) => (
                <tr key={mem.id} className="border-b border-zinc-100 last:border-0">
                  <td className="px-4 py-2 font-mono text-xs">{mem.email}</td>
                  <td className="px-4 py-2">{mem.full_name || '—'}</td>
                  <td className="px-4 py-2"><span className="text-xs uppercase tracking-widest">{mem.role}</span></td>
                  <td className="px-4 py-2 font-mono text-xs text-zinc-500">{new Date(mem.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {memberMsg && !memberModalOpen && (
          <div className={`mt-3 text-xs font-mono ${memberMsg.type==='ok' ? 'text-green-700' : 'text-red-700'}`}>
            {memberMsg.text}
          </div>
        )}
      </section>

      {memberModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 px-4">
          <div className="w-full max-w-lg border border-zinc-200 bg-white p-5 shadow-sm">
            <h2 className="font-display text-lg font-semibold">Add member</h2>
            <p className="mt-2 text-sm leading-6 text-zinc-600">Add a new member to this workspace.</p>

            <form onSubmit={addMember} className="mt-5 grid gap-4" data-testid="add-member-form">
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Email</span>
                <input required type="email" value={m.email} onChange={(e)=>setM({...m, email:e.target.value})}
                  data-testid="member-email" className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Password</span>
                <input required type="password" value={m.password} onChange={(e)=>setM({...m, password:e.target.value})}
                  data-testid="member-password" className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Full name</span>
                <input value={m.full_name} onChange={(e)=>setM({...m, full_name:e.target.value})}
                  data-testid="member-name" className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Role</span>
                <select value={m.role} onChange={(e)=>setM({...m, role:e.target.value})} data-testid="member-role"
                  className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black">
                  <option value="reviewer">reviewer</option>
                  <option value="admin">admin</option>
                </select>
              </label>
              {memberMsg && (
                <div className={`text-xs font-mono ${memberMsg.type==='ok' ? 'text-green-700' : 'text-red-700'}`}>
                  {memberMsg.text}
                </div>
              )}
              <div className="mt-1 flex justify-end gap-2">
                <button type="button" onClick={() => {
                  setMemberModalOpen(false);
                  setMemberMsg(null);
                }}
                  className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400">
                  Cancel
                </button>
                <button type="submit" data-testid="add-member-submit" className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800">
                  Add member
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isAdmin && (
        <section className="border border-red-200 rounded-sm p-6" data-testid="danger-zone">
          <h2 className="font-display text-lg font-semibold text-red-800">Danger zone</h2>
          <p className="text-sm text-zinc-600 mt-1 max-w-xl">
            Delete this organization and permanently remove all users, verifications, documents, signatures, and settings.
          </p>
          <button type="button" onClick={() => { setDeleteOpen(true); setDeleteConfirm(''); setDeleteError(null); }}
            className="mt-4 inline-flex items-center gap-1.5 text-sm px-3 py-2 border border-red-200 text-red-700 rounded-sm hover:border-red-400">
            <Trash2 className="w-4 h-4" strokeWidth={1.5}/> Delete organization
          </button>
        </section>
      )}

      {deleteOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4" onClick={() => setDeleteOpen(false)}>
          <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-md p-6" onClick={(event) => event.stopPropagation()}>
            <h2 className="font-display text-xl font-semibold">Delete organization?</h2>
            <p className="text-sm text-zinc-600 mt-2">
              This action is permanent and cannot be undone. All verifications, documents, and data will be deleted.
            </p>
            <label className="block mt-5 text-sm">
              <span className="text-xs uppercase tracking-widest text-zinc-500">Type your organization name to confirm</span>
              <input value={deleteConfirm} onChange={(event) => setDeleteConfirm(event.target.value)}
                className="mt-2 w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-red-500"
                autoFocus/>
            </label>
            {deleteError && <p className="mt-3 text-sm text-red-700">{deleteError}</p>}
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteOpen(false)} disabled={deleteBusy}
                className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400 disabled:opacity-50">
                Cancel
              </button>
              <button type="button" onClick={deleteOrganization}
                disabled={deleteBusy || deleteConfirm !== data.organization.name}
                className="text-sm px-3 py-2 bg-red-700 text-white rounded-sm hover:bg-red-800 disabled:opacity-40">
                {deleteBusy ? 'Deleting...' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
